import React, {useState, useEffect, useRef} from 'react'
import { Wrapper, Header } from "./style";
import { useSelector } from 'react-redux';
import platform from 'platform';
import { useRouter } from 'next/router';
import lodashDebounce from 'lodash.debounce';

// Components
import Image from "next/image";
import ClinicInfo from './ClinicInfo';
import Specialty from './Specialty';
import Services from './Services';
import DoctorBio from './DoctorBio';
import BlogPosts from './BlogPosts';
import FreeTimesCalendar from 'components/common/FreeTimesCalendar';
import Button from 'components/common/Button';
import Text from 'components/common/Text';

// Libs
import Translation from 'libs/Translation';
import ModalManager from 'libs/ModalManager';

const DoctorProfile = ({ doctor, webConfig }) => {
    const router = useRouter();
    const ref = useRef(null)
    const { showOnlineFreeTime, showInPersonFreeTime, serviceId, healthProviderId } = router.query;
    const {appPromptClose} = useSelector(state => state.core);
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarTop, setSidebarTop] = useState();
    const [height, setHeight] = useState();
    const [secondHeight, setSecondHeight] = useState();

	// Detect device type
    useEffect(() => {
		setIsMobile(['Android', 'iOS'].includes(platform.os?.family));
	}, []);

    const handleShowCalendar = (onlineService, serviceId, healthProviderId) => {
        ModalManager.show({
            content: <FreeTimesCalendar 
                        doctor={doctor?.profiles}
                        webConfig={webConfig} 
                        hideBookButton={true}
                        nextStepButton={true}
                        onlineService={onlineService}
                        serviceId={serviceId}
                        healthProviderId={healthProviderId}
                    />,
            title: 
                <Header>
                    <Image
                        src={doctor?.profiles?.profileImage ? doctor?.profiles?.profileImage?.fileUrl : '/nextasset/images/doctorProfile/Avatar.png'}
                        alt={`${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`}
                        width={58}
                        height={58}
                        className="img"
                    />
                    <div className='column-flex'>
                        <Text
                            className="name"
                        >
                            {`${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`}
                        </Text>
                        {doctor?.specialtyDetails?.length ?
                        <Text
                            className='specialties'
                        >
                            {doctor?.specialtyDetails.map((s, i) => 
                            <>
                            {Translation.t('label.specialty-education', {degree: s.EducationLevel, specialty: s.Specialty})}
                            {i !== doctor.specialtyDetails.length - 1 &&
                            <>
                                <span>{Translation.t(',')}</span>
                                {' '}
                            </>
                            }
                            </>
                            )}
                        </Text>
                        : null}  
                    </div>
                </Header>,
            maxWidth: 'sm',
            background: '#F5F5F7',
            fullScreenMobile: true,
            hideCloseButton: false,
            closable: true,
        })
    }

    // Show services free time based on serviceId, showOnlineFreeTime and showInPersonFreeTime parameters
    useEffect(() => {
        if (showOnlineFreeTime === 'true') {
            handleShowCalendar(true);
        }
        if (showInPersonFreeTime === 'true') {
            handleShowCalendar(false);
        }
        if (serviceId) {
            handleShowCalendar(null, serviceId);
        }
        if (healthProviderId) {
            handleShowCalendar(null, null, healthProviderId);
        }
    }, [router])


    // Set free time section height
    const debouncedSetHeight = lodashDebounce(() => {setSecondHeight(ref?.current?.clientHeight)}, 3000)
    useEffect(() => {
        debouncedSetHeight();
    }, [router])
    
    useEffect(() => {
        const secColumn = document.querySelector('.second-column').getBoundingClientRect();
        const column = document.querySelector('.first-column').getBoundingClientRect();
        setHeight(column.height);
        setSidebarTop(secColumn.top);
    }, [router]);
    
    useEffect(() => {
        if (!sidebarTop || !secondHeight) return;
    
        window.addEventListener('scroll', isSticky);
        return () => {
            window.removeEventListener('scroll', isSticky);
        };
    }, [sidebarTop, secondHeight]);

    const isSticky = (e) => {
            const secColumn = document.querySelector('.second-column');
            const scrollTop = window.scrollY;
            if (scrollTop >= 60) {
                if (scrollTop - 60 < height - secondHeight) {
                    secColumn.classList.add('is-sticky');
                    secColumn.classList.remove('is-fixed');
                }
                else {
                    secColumn.classList.remove('is-sticky');
                    secColumn.classList.add('is-fixed');
                }
            } 
            else {
                secColumn.classList.remove('is-sticky');
                secColumn.classList.remove('is-fixed');
            }
    };

    return (
        <Wrapper 
            webConfig={webConfig}
            appPromptClose={appPromptClose}
            isMobile={isMobile}
        >
            <div className="first-column">
                <div className="box">
                    <div className='column-flex flex-start'>
                        <div className="doctor-image">
                            <Image
                                src={doctor?.profiles?.profileImage ? doctor?.profiles?.profileImage?.fileUrl : '/nextasset/images/doctorProfile/Avatar.png'}
                                alt={`${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`}
                                width={96}
                                height={96}
                                priority={true}
                                className="img"
                            />
                        </div>
                        
                        <Text 
                            color="#0D2439"
                            className="name"
                        >
                            {`${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`}
                        </Text>
                    </div>
                    <ClinicInfo 
                        doctor={doctor?.profiles}
                        webConfig={webConfig}
                    />
                </div>

                {doctor?.specialtyDetails?.length > 0 &&
                <Specialty 
                    doctor={doctor}
                    webConfig={webConfig} 
                />
                }

                {doctor?.profiles?.Services?.length > 0 &&
                <Services 
                    doctor={doctor.profiles}
                    webConfig={webConfig}
                />
                }

                {doctor?.profiles?.Biography?.length > 0 &&
                <DoctorBio 
                    doctor={doctor.profiles} 
                    webConfig={webConfig}
                />
                }

                <BlogPosts 
                    doctor={doctor}
                    webConfig={webConfig} 
                />     
            </div>

            <div 
                className="second-column" 
                ref={ref}
            >
                <FreeTimesCalendar 
                    doctor={doctor?.profiles}
                    webConfig={webConfig} 
                    title
                />
            </div>

            <div 
                className='fixed-button'
                onClick={() => handleShowCalendar(false)}
            >
                <Button
                    className="button"
                >
                    {Translation.t('label.book-date')}
                </Button>
            </div>
        </Wrapper>
    )
}
export default DoctorProfile;